import { FieldTranslator } from '../FieldTranslator';
import { TaskManager } from '../TaskManager';
import { BaseResourceManager } from '../BaseResourceManager';
import { SchoolField, ResoResource } from '../../system/reso';
import { useReso } from '../../contexts/ResoContext';
import { ResourceManagerOptions } from '../types';

// Field name mapping for School
const FIELD_MAPPING: Record<string, string> = {
  [SchoolField.KEY]: 'SCHOOL_KEY',
  [SchoolField.NAME]: 'SCHOOL_NAME',
  [SchoolField.TYPE]: 'SCHOOL_TYPE',
  [SchoolField.COUNTY]: 'SCHOOL_COUNTY',
  [SchoolField.DISTRICT_NAME]: 'SCHOOL_DISTRICT_NAME',
  [SchoolField.DISTRICT_KEY]: 'SCHOOL_DISTRICT_KEY',
  [SchoolField.MODIFICATION_TIMESTAMP]: 'SCHOOL_MODIFICATION_TIMESTAMP',
};

export class SchoolResource implements Record<string, any> {
  [key: string]: any;

  constructor(data: Record<string, any> = {}) {
    // Copy all provided data to this instance
    Object.assign(this, data);
  }

  getKey(): string | number | undefined {
    return this[FIELD_MAPPING[SchoolField.KEY]];
  }

  getItemNumber(): number | undefined {
    return undefined; // Schools don't have an item number
  }

  getListingKeyField(): string | undefined {
    return undefined; // Schools are not directly related to listings
  }

  getListingKey(): string | undefined {
    return undefined; // Schools are not directly related to listings
  }

  // Utility methods for school resources
  getName(): string | undefined {
    return this[FIELD_MAPPING[SchoolField.NAME]];
  }

  getType(): string | undefined {
    return this[FIELD_MAPPING[SchoolField.TYPE]];
  }

  getCounty(): string | undefined {
    return this[FIELD_MAPPING[SchoolField.COUNTY]];
  }

  getDistrictName(): string | undefined {
    return this[FIELD_MAPPING[SchoolField.DISTRICT_NAME]];
  }

  getDistrictKey(): string | undefined {
    return this[FIELD_MAPPING[SchoolField.DISTRICT_KEY]];
  }
}

export class SchoolResourceManager extends BaseResourceManager<SchoolResource> {
  constructor(
    executeQuery: ReturnType<typeof useReso>['executeQuery'],
    fieldTranslator: FieldTranslator,
    taskManager: TaskManager,
    options: ResourceManagerOptions = {},
  ) {
    super(ResoResource.SCHOOL, executeQuery, fieldTranslator, taskManager, {
      ...options,
      useTranslation: false, // Disable translation
    });
  }

  /**
   * Map field name using our custom mapping
   */
  protected mapFieldName(field: string): string {
    return FIELD_MAPPING[field] || field;
  }

  /**
   * Core mapping function from API data to SchoolResource
   */
  protected mapFromApiCore(apiData: any): SchoolResource {
    return new SchoolResource(apiData);
  }

  /**
   * Core mapping function from SchoolResource to API data
   */
  protected mapToApiCore(resource: SchoolResource): any {
    // Return a clean copy of the resource
    return { ...resource };
  }

  /**
   * Find schools by name
   */
  public async findByName(name: string): Promise<SchoolResource[]> {
    return this.findListByField(SchoolField.NAME, name);
  }

  /**
   * Find schools by county
   */
  public async findByCounty(county: string): Promise<SchoolResource[]> {
    return this.findListByField(SchoolField.COUNTY, county);
  }

  /**
   * Find schools by type
   */
  public async findByType(type: string): Promise<SchoolResource[]> {
    return this.findListByField(SchoolField.TYPE, type);
  }

  /**
   * Find schools by district
   */
  public async findByDistrict(districtName: string): Promise<SchoolResource[]> {
    return this.findListByField(SchoolField.DISTRICT_NAME, districtName);
  }

  /**
   * Find schools by district and type
   */
  public async findByDistrictAndType(
    districtName: string,
    type: string,
  ): Promise<SchoolResource[]> {
    return this.findListByFields({
      [SchoolField.DISTRICT_NAME]: districtName,
      [SchoolField.TYPE]: type,
    });
  }

  /**
   * Find schools by county and type
   */
  public async findByCountyAndType(
    county: string,
    type: string,
  ): Promise<SchoolResource[]> {
    return this.findListByFields({
      [SchoolField.COUNTY]: county,
      [SchoolField.TYPE]: type,
    });
  }

  /**
   * Override findListByField to use our field mapping
   */
  public async findListByField(
    field: string,
    value: any,
  ): Promise<SchoolResource[]> {
    const mappedField = this.mapFieldName(field);
    return super.findListByField(mappedField, value);
  }

  /**
   * Override findByField to use our field mapping
   */
  public async findByField(
    field: string,
    value: any,
  ): Promise<SchoolResource | null> {
    const mappedField = this.mapFieldName(field);
    return super.findByField(mappedField, value);
  }
}

/**
 * Create a new SchoolResource instance with required fields
 */
export function createSchoolResource(
  data: Partial<Record<string, any>> = {},
): SchoolResource {
  const defaults = {};

  return new SchoolResource({
    ...defaults,
    ...data,
  });
}
